import React, { useState, useEffect } from 'react'
import { useAuth } from '../../../context/AuthProvider'
import { useUserRole } from '../../../context/UserRoleProvider'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import RefundRequestModal from '../../../components/donations/RefundRequestModal'
import { Input } from '../../../components/ui/input'
import { Loader2, TrendingUp, Users, DollarSign, Calendar, CreditCard, Download, Filter, X, ChevronUp, ChevronDown } from 'lucide-react'

// Weekly Line Chart Component
function WeeklyLineChart({ data, formatCurrency }) {
  const [hoveredIndex, setHoveredIndex] = useState(null)
  
  if (!data || data.length === 0) return null

  // Reverse to show oldest to newest (left to right)
  const sortedData = [...data].reverse()
  const maxAmount = Math.max(...sortedData.map(d => d.amount), 1)
  const minAmount = Math.min(...sortedData.map(d => d.amount), 0)
  const range = maxAmount - minAmount || 1

  const width = 800
  const height = 300
  const padding = { top: 20, right: 40, bottom: 60, left: 80 }
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom

  // Calculate points for the line
  const points = sortedData.map((week, index) => {
    const x = padding.left + (index / (sortedData.length - 1 || 1)) * chartWidth
    const y = padding.top + chartHeight - ((week.amount - minAmount) / range) * chartHeight
    return { x, y, ...week }
  })

  // Create path for the line
  const pathData = points.map((point, index) => {
    return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  }).join(' ')

  // Format week label as date range
  const formatWeekLabel = (weekStart, weekEnd) => {
    const start = new Date(weekStart)
    const end = new Date(weekEnd)
    const startStr = `${start.getMonth() + 1}/${start.getDate()}`
    const endStr = `${end.getMonth() + 1}/${end.getDate()}`
    return `${startStr}-${endStr}`
  }

  return (
    <div className="w-full overflow-x-auto">
      <svg width={width} height={height} className="w-full" viewBox={`0 0 ${width} ${height}`}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = padding.top + chartHeight - (ratio * chartHeight)
          const value = minAmount + (ratio * range)
          return (
            <g key={ratio}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="currentColor"
                strokeWidth="1"
                className="text-charcoal-faint"
              />
              <text
                x={padding.left - 10}
                y={y + 4}
                textAnchor="end"
                className="text-xs fill-gold/60"
              >
                {formatCurrency(value)}
              </text>
            </g>
          )
        })}

        {/* Line */}
        <path
          d={pathData}
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          className="text-charcoal"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Points */}
        {points.map((point, index) => {
          // Determine tooltip position - show below if near top, above if near bottom
          const isNearTop = point.y < padding.top + 80
          const tooltipY = isNearTop ? point.y + 60 : point.y - 50
          const tooltipHeight = 50
          const isHovered = hoveredIndex === index
          
          return (
            <g key={index}>
              {/* Invisible larger circle for hover area */}
              <circle
                cx={point.x}
                cy={point.y}
                r="15"
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
              {/* Visible point */}
              <circle
                cx={point.x}
                cy={point.y}
                r="5"
                fill="currentColor"
                className="text-charcoal pointer-events-none"
              />
              {/* Tooltip */}
              {isHovered && (
                <g className="pointer-events-none">
                  <rect
                    x={point.x - 60}
                    y={tooltipY}
                    width="120"
                    height={tooltipHeight}
                    rx="4"
                    className="fill-primary stroke-gold/50"
                    strokeWidth="1"
                  />
                  <text
                    x={point.x}
                    y={tooltipY + 20}
                    textAnchor="middle"
                    className="text-xs fill-gold font-semibold"
                  >
                    {formatCurrency(point.amount)}
                  </text>
                  <text
                    x={point.x}
                    y={tooltipY + 35}
                    textAnchor="middle"
                    className="text-xs fill-gold/70"
                  >
                    {point.count} {point.count === 1 ? 'donation' : 'donations'}
                  </text>
                </g>
              )}
            </g>
          )
        })}

        {/* X-axis labels */}
        {points.map((point, index) => (
          <g key={index}>
            <text
              x={point.x}
              y={height - padding.bottom + 20}
              textAnchor="middle"
              className="text-xs fill-gold/70"
            >
              {formatWeekLabel(point.weekStart, point.weekEnd)}
            </text>
          </g>
        ))}
      </svg>
    </div>
  )
}

const VISIBLE_DONATION_COUNT = 3

function formatCurrency(cents) {
  return `$${(cents / 100).toFixed(2)}`
}

function formatDateTime(dateString) {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Pivots each donation's customFields (keyed by fieldId, ordered
// alphabetically by snapshot label — see Donations.jsx history) into one CSV
// column per field definition. Columns are keyed by fieldId, not label:
// labels are NOT guaranteed unique within a donation (an archived field and
// its replacement can share label text), so headers built purely from label
// text can collide. We disambiguate in two passes:
//   1. An archived field's header is suffixed " (archived)" so a
//      replacement field with the same label reads distinctly in the common
//      case (one active + one archived sharing a label).
//   2. If headers still collide after that (e.g. two archived fields that
//      share a label, or two active fields with identical labels), we
//      number the later occurrences " (2)", " (3)", etc. so every header is
//      unique and Excel/Sheets doesn't silently merge the columns.
export function buildDonationFieldColumns(donations, fields) {
  const answeredFieldIds = new Set()
  donations.forEach(d => (d.customFields || []).forEach(v => answeredFieldIds.add(v.fieldId)))

  // Active fields always get a column; an archived field only earns one when
  // a donation in this range actually used it.
  const columns = [...(fields || [])]
    .filter(f => !f.archivedAt || answeredFieldIds.has(f.id))
    .sort((a, b) => {
      const archivedDiff = Number(Boolean(a.archivedAt)) - Number(Boolean(b.archivedAt))
      return archivedDiff !== 0 ? archivedDiff : a.sortOrder - b.sortOrder
    })

  const seenHeaders = new Map()
  return columns.map(f => {
    const base = f.archivedAt ? `${f.label} (archived)` : f.label
    const count = seenHeaders.get(base) || 0
    seenHeaders.set(base, count + 1)
    return {
      fieldId: f.id,
      header: count === 0 ? base : `${base} (${count + 1})`,
      archived: Boolean(f.archivedAt),
    }
  })
}

// CSV-escapes a single cell: doubles embedded quotes (existing behavior)
// and, if the value starts with a character a spreadsheet app would treat
// as the start of a formula (=, +, -, @) or that lets a value bleed across
// lines/cells (tab, carriage return), prefixes it with a leading apostrophe
// so it lands in the cell as inert text instead of evaluating. formatCurrency
// always prepends "$" — even to negative amounts ("$-5.00") — so ordinary
// currency cells never trip the "-" branch; this only affects raw values
// (e.g. a donor-entered NUMBER field like "-5") that legitimately start
// with "-". Those export as "'-5" (a leading apostrophe, same as the
// formula case) even though the on-screen table still shows "-5" plain —
// that's the accepted cost of the mitigation, not a bug, and not worth a
// special case: the value still reads correctly, just as text rather than
// a number, when the CSV is opened.
// One helper is shared by the header line and every data row so they can't
// drift out of sync on quoting.
export function escapeCsvCell(value) {
  let str = String(value)
  if (/^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`
  }
  return `"${str.replace(/"/g, '""')}"`
}

export function refundStatusLabel(d) {
  if (d.status === 'refunded') return 'Refunded'
  if (d.status === 'succeeded' && (d.refundedAmount || 0) > 0) return 'Partially refunded'
  const raw = String(d.status || 'succeeded')
  return raw.charAt(0).toUpperCase() + raw.slice(1)
}

const REQUEST_STATUS_LABELS = {
  requested: 'Requested',
  approved: 'Approved',
  rejected: 'Rejected',
  processed: 'Processed',
  failed: 'Failed',
}

export function buildDonationCsvRows(donations, fields) {
  const baseHeaders = ['Date', 'Donor Name', 'Donor Email', 'Gross Amount', 'Fees', 'Net Received', 'Type', 'Payment Method', 'Status']

  // The expanded table renders these same columns, from this same call. Two
  // column builders would drift; there is only one.
  const columns = buildDonationFieldColumns(donations, fields)

  const headers = [...baseHeaders, ...columns.map(c => c.header)]

  const rows = donations.map(d => [
    formatDateTime(d.completedAt || d.createdAt),
    d.userName,
    d.userEmail,
    formatCurrency(d.amount),
    formatCurrency(d.applicationFeeAmount ?? 0),
    formatCurrency(d.netAmount ?? d.amount),
    d.donationType === 'one-time' ? 'One-Time' : 'Recurring',
    d.paymentMethodLast4 ? `•••• ${d.paymentMethodLast4}` : 'N/A',
    refundStatusLabel(d),
    ...columns.map(column => {
      const match = (d.customFields || []).find(v => v.fieldId === column.fieldId)
      return match ? match.value : ''
    }),
  ])

  return { headers, rows }
}

export default function ParishDonations() {
  const { user } = useAuth()
  const { getParishAdminParishId } = useUserRole()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dashboard, setDashboard] = useState(null)
  const [parishId, setParishId] = useState(null)
  const [dateRange, setDateRange] = useState('all') // 'all', 'today', 'week', 'month', 'year', 'custom'
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [customRangeSubmitKey, setCustomRangeSubmitKey] = useState(0)
  const [donationTypeFilter, setDonationTypeFilter] = useState('all')
  const [exporting, setExporting] = useState(false)
  const [showAllDonations, setShowAllDonations] = useState(false)
  const [fields, setFields] = useState([])
  const [refundRequests, setRefundRequests] = useState([])
  const [refundTarget, setRefundTarget] = useState(null)
  const [fieldSummary, setFieldSummary] = useState([])
  // ONE custom field filter at a time — the API takes a single field/value
  // pair, and the list on screen and the CSV export are both driven from this
  // same state, so an export can never disagree with what is being looked at.
  const [customFieldFilter, setCustomFieldFilter] = useState({ fieldId: '', value: '' })
  // What the admin is typing into a TEXT field's search box, before debounce.
  const [textDraft, setTextDraft] = useState({ fieldId: '', value: '' })

  // Debounce typing so every keystroke does not refetch the dashboard. A
  // dropdown selection resets the draft's fieldId, which parks this effect —
  // otherwise it would stomp the dropdown filter 300ms later.
  useEffect(() => {
    if (!textDraft.fieldId) return undefined
    const handle = setTimeout(() => {
      const value = textDraft.value.trim()
      const fieldId = value ? textDraft.fieldId : ''
      setCustomFieldFilter(prev =>
        prev.fieldId === fieldId && prev.value === value ? prev : { fieldId, value }
      )
    }, 300)
    return () => clearTimeout(handle)
  }, [textDraft])

  const selectCustomFieldFilter = (fieldId, value) => {
    setTextDraft({ fieldId: '', value: '' })
    setCustomFieldFilter(value ? { fieldId, value } : { fieldId: '', value: '' })
  }

  const customFieldParams = customFieldFilter.fieldId && customFieldFilter.value
    ? {
        customFieldId: customFieldFilter.fieldId,
        customFieldValue: customFieldFilter.value,
      }
    : {}

  const getDateRangeParams = () => {
    if (dateRange === 'all') {
      return {}
    } else if (dateRange === 'custom') {
      return {
        startDate: startDate ? new Date(startDate).toISOString() : undefined,
        endDate: endDate ? new Date(endDate + 'T23:59:59').toISOString() : undefined,
      }
    } else {
      const now = new Date()
      let start
      
      if (dateRange === 'today') {
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      } else if (dateRange === 'week') {
        start = new Date(now)
        start.setDate(now.getDate() - now.getDay()) // Start of week (Sunday)
        start.setHours(0, 0, 0, 0)
      } else if (dateRange === 'month') {
        start = new Date(now.getFullYear(), now.getMonth(), 1)
      } else if (dateRange === 'year') {
        start = new Date(now.getFullYear(), 0, 1)
      } else {
        return {}
      }
      
      return {
        startDate: start.toISOString(),
        endDate: now.toISOString(),
      }
    }
  }

  const fetchDashboardData = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      setError(null)

      // Get parish ID for parish admin
      let id = getParishAdminParishId()
      
      if (!id) {
        // Fallback: try to get from API
        const userData = await api.getMe()
        const roleDetails = userData?.roleDetails || []
        const parishAdminRole = roleDetails.find(r => r.name === 'parish-admin')
        id = parishAdminRole?.parishId
      }

      if (!id) {
        setError('You are not associated with a parish as a parish admin.')
        setLoading(false)
        return
      }

      setParishId(id)

      // Get date range parameters
      const dateParams = getDateRangeParams()

      // Fetch dashboard data with date range, type and custom field filters.
      // Field definitions and the per-option totals ride alongside; neither is
      // worth failing the whole page over.
      const [dashboardData, fieldData, summaryData, requestData] = await Promise.all([
        api.getParishDonationDashboard(id, {
          ...dateParams,
          donationType: donationTypeFilter !== 'all' ? donationTypeFilter : undefined,
          ...customFieldParams,
        }),
        api.getParishDonationFields(id, { includeArchived: true }).catch(() => []),
        api.getParishDonationFieldSummary(id, {
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
          // The SAME type filter the list uses. Without it the totals block —
          // and the match count built from it — would describe a population
          // wider than the one on screen.
          donationType: donationTypeFilter !== 'all' ? donationTypeFilter : undefined,
        }).catch(() => []),
        Promise.resolve(api.listParishRefundRequests(id)).catch(() => []),
      ])
      setRefundRequests(requestData || [])
      setDashboard(dashboardData)
      setFields(fieldData || [])
      setFieldSummary(summaryData || [])
    } catch (err) {
      console.error('Failed to fetch donation dashboard:', err)
      setError(err.message || 'Failed to load donation dashboard')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDashboardData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user?.id,
    dateRange,
    customRangeSubmitKey,
    donationTypeFilter,
    customFieldFilter.fieldId,
    customFieldFilter.value,
  ])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const reloadRefundRequests = async () => {
    if (!parishId) return
    try {
      const next = await api.listParishRefundRequests(parishId)
      if (Array.isArray(next)) setRefundRequests(next)
    } catch (err) {
      // Keep the previous list rather than blanking every badge.
      console.error('Failed to reload refund requests:', err)
    }
  }

  const handleRefundSubmit = async ({ reason, amountCents }) => {
    await api.createRefundRequest(parishId, refundTarget.id, {
      reason,
      ...(amountCents != null && { amountCents }),
    })
    setRefundTarget(null)
    await reloadRefundRequests()
  }

  const exportToCSV = async () => {
    if (!parishId) {
      alert('Parish ID not available')
      return
    }

    try {
      setExporting(true)

      // Get date range parameters
      const dateParams = getDateRangeParams()

      // Fetch all donations by paginating through all pages with date filtering
      let allDonations = []
      let page = 1
      let hasMore = true
      const limit = 100 // Fetch in batches of 100

      // Field definitions (including archived ones, so archived columns with
      // in-range values can still be labeled) are fetched once here, not
      // per page.
      const fieldsPromise = fields.length
        ? Promise.resolve(fields)
        : api.getParishDonationFields(parishId, { includeArchived: true })

      while (hasMore) {
        // The SAME custom field filter the list on screen is using. The list
        // and the export are two different endpoints; if only one of them were
        // filtered the CSV would silently disagree with what is on screen.
        const response = await api.getParishDonations(parishId, {
          page,
          limit,
          startDate: dateParams.startDate,
          endDate: dateParams.endDate,
          donationType: donationTypeFilter !== 'all' ? donationTypeFilter : undefined,
          ...customFieldParams
        })
        const donations = response.data || []

        allDonations = allDonations.concat(donations)

        // Check if there are more pages
        hasMore = response.meta?.hasNextPage || false
        page++
      }

      if (allDonations.length === 0) {
        alert('No donations found for the selected date range')
        return
      }

      // Sort by date (most recent first)
      allDonations.sort((a, b) => {
        const dateA = a.completedAt ? new Date(a.completedAt) : new Date(a.createdAt)
        const dateB = b.completedAt ? new Date(b.completedAt) : new Date(b.createdAt)
        return dateB - dateA
      })

      // Named apart from the `fields` state above — a local `fields` here would
      // shadow it and put the state read earlier in this function in the TDZ.
      const fieldDefs = (await fieldsPromise) || []

      // Gross is what the donor gave (the receipt figure); fees and net show
      // what was deducted and what the parish actually received.
      const { headers, rows } = buildDonationCsvRows(allDonations, fieldDefs)

      const csvContent = [
        headers.map(escapeCsvCell).join(','),
        ...rows.map(row => row.map(escapeCsvCell).join(','))
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      
      // Create filename with date range if applicable
      let filename = `donations-${new Date().toISOString().split('T')[0]}`
      if (dateParams.startDate || dateParams.endDate) {
        const startStr = dateParams.startDate ? new Date(dateParams.startDate).toISOString().split('T')[0] : 'all'
        const endStr = dateParams.endDate ? new Date(dateParams.endDate).toISOString().split('T')[0] : 'all'
        filename = `donations-${startStr}-to-${endStr}`
      }
      a.download = `${filename}.csv`
      
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to export donations:', err)
      alert(`Failed to export donations: ${err.message || 'Unknown error'}`)
    } finally {
      setExporting(false)
    }
  }

  // Calculate percentage change (mock - you could compare with previous period)
  const getPercentageChange = (current, previous = 0) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return ((current - previous) / previous) * 100
  }

  // Only the FIRST load blanks the page. Refetching for a filter must not tear
  // the controls out from under the admin who is using them.
  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  if (!dashboard) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white border border-surface-border rounded p-6">
          <p className="text-charcoal-muted">No donation data available.</p>
        </div>
      </div>
    )
  }

  const handleDateRangeChange = (range) => {
    setDateRange(range)
    if (range !== 'custom') {
      setStartDate('')
      setEndDate('')
      setCustomRangeSubmitKey(0)
    } else {
      // Reset submit key when switching to custom
      setCustomRangeSubmitKey(0)
    }
  }

  const handleCustomRangeSubmit = () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates')
      return
    }
    if (new Date(startDate) > new Date(endDate)) {
      alert('Start date must be before or equal to end date')
      return
    }
    // Increment the key to trigger useEffect
    setCustomRangeSubmitKey(prev => prev + 1)
  }

  const clearDateFilter = () => {
    setDateRange('all')
    setStartDate('')
    setEndDate('')
    setCustomRangeSubmitKey(0)
  }

  // Per-option totals, keyed by field id. Used both for the totals block and to
  // decide which ARCHIVED dropdowns are still worth offering as filters: one
  // with answers in this range is exactly the memorial-tracking case.
  const summaryByFieldId = new Map(fieldSummary.map(entry => [entry.fieldId, entry]))
  const filterableFields = fields.filter(f => !f.archivedAt || summaryByFieldId.has(f.id))
  const recentDonations = dashboard.recentDonations || []
  // Newest request per donation.
  const latestRequestByDonation = new Map()
  for (const r of refundRequests) {
    const prev = latestRequestByDonation.get(r.donationId)
    if (!prev || new Date(r.createdAt) > new Date(prev.createdAt)) {
      latestRequestByDonation.set(r.donationId, r)
    }
  }
  // Columns are built by the SAME function the CSV uses, so the expanded table
  // and the export cannot drift apart. Keyed by fieldId, never by label.
  const donationFieldColumns = showAllDonations
    ? buildDonationFieldColumns(recentDonations, fields)
    : []

  // The real number of completed donations the active filter matches, from the
  // summary already fetched. Only a dropdown filter has one — free text is not
  // summarised.
  const activeFilterMatchCount = customFieldFilter.fieldId
    ? summaryByFieldId
        .get(customFieldFilter.fieldId)
        ?.options.find(o => o.value === customFieldFilter.value)?.count ?? null
    : null

  const formatDollars = (cents) =>
    `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const filterOptionsFor = (field) => {
    if (field.options && field.options.length > 0) return field.options
    // An archived field's definition may no longer carry its options; the
    // values that were actually answered in range still do.
    return (summaryByFieldId.get(field.id)?.options || []).map(o => ({ value: o.value, label: o.label }))
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-display text-charcoal">Donation Dashboard</h1>
          <p className="text-sm text-charcoal-muted mt-1">
            Totals show what your parish received, after platform and payment processing fees.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Filters */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-charcoal-muted" />
            <select
              value={donationTypeFilter}
              onChange={(e) => setDonationTypeFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-surface-border rounded text-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="all">All Types</option>
              <option value="one-time">One-Time</option>
              <option value="recurring">Recurring</option>
              <option value="roundup">Round-Up</option>
            </select>
            <select
              value={dateRange}
              onChange={(e) => handleDateRangeChange(e.target.value)}
              className="px-3 py-2 bg-white border border-surface-border rounded text-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            {(dateRange !== 'all' || donationTypeFilter !== 'all' || customFieldFilter.fieldId) && (
              <Button
                onClick={() => {
                  clearDateFilter()
                  setDonationTypeFilter('all')
                  selectCustomFieldFilter('', '')
                }}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Clear
              </Button>
            )}
          </div>
          <Button 
            onClick={exportToCSV} 
            variant="outline" 
            className="flex items-center gap-2"
            disabled={exporting}
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export CSV
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Custom Date Range Inputs */}
      {dateRange === 'custom' && (
        <div className="bg-white border border-surface-border rounded p-4 mb-6">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-charcoal mb-2">
                Start Date
              </label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-surface-linen border-surface-border text-charcoal"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-charcoal mb-2">
                End Date
              </label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-surface-linen border-surface-border text-charcoal"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div>
              <Button
                onClick={handleCustomRangeSubmit}
                className="flex items-center gap-2"
                disabled={!startDate || !endDate}
              >
                Apply Filter
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Donations */}
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-5 h-5 text-charcoal" />
            <span className="text-xs text-charcoal-muted">All Time</span>
          </div>
          <div className="text-2xl font-bold text-charcoal mb-1">
            {formatCurrency(dashboard.totalAmount)}
          </div>
          <div className="text-sm text-charcoal-muted">
            {dashboard.totalCount} {dashboard.totalCount === 1 ? 'donation' : 'donations'} · after fees
          </div>
        </div>

        {/* Total Donors */}
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-5 h-5 text-charcoal" />
            <span className="text-xs text-charcoal-muted">Unique</span>
          </div>
          <div className="text-2xl font-bold text-charcoal mb-1">
            {dashboard.uniqueDonors}
          </div>
          <div className="text-sm text-charcoal-muted">
            {dashboard.uniqueDonors === 1 ? 'donor' : 'donors'}
          </div>
        </div>

        {/* This Month */}
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center justify-between mb-2">
            <Calendar className="w-5 h-5 text-charcoal" />
            <span className="text-xs text-charcoal-muted">This Month</span>
          </div>
          <div className="text-2xl font-bold text-charcoal mb-1">
            {formatCurrency(dashboard.thisMonthAmount)}
          </div>
          <div className="text-sm text-charcoal-muted">
            Avg: {formatCurrency(dashboard.averageDonationAmount)}
          </div>
        </div>

        {/* This Year */}
        <div className="bg-white border border-surface-border rounded p-6">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-5 h-5 text-charcoal" />
            <span className="text-xs text-charcoal-muted">This Year</span>
          </div>
          <div className="text-2xl font-bold text-charcoal mb-1">
            {formatCurrency(dashboard.thisYearAmount)}
          </div>
          <div className="text-sm text-charcoal-muted">
            {dashboard.thisMonthAmount > 0 && (
              <span className="text-green-400">
                {((dashboard.thisMonthAmount / dashboard.thisYearAmount) * 100).toFixed(1)}% this month
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Time Period Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Today */}
        <div className="bg-white border border-surface-border rounded p-6">
          <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-charcoal" />
            Today
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-charcoal-muted">Amount</span>
              <span className="text-xl font-bold text-charcoal">{formatCurrency(dashboard.todayAmount)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-charcoal-muted">Donations</span>
              <span className="text-lg font-semibold text-charcoal">{dashboard.todayCount}</span>
            </div>
          </div>
        </div>

        {/* This Week */}
        <div className="bg-white border border-surface-border rounded p-6">
          <h3 className="text-lg font-semibold text-charcoal mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-charcoal" />
            This Week
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-charcoal-muted">Amount</span>
              <span className="text-xl font-bold text-charcoal">{formatCurrency(dashboard.thisWeekAmount)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-charcoal-muted">Donations</span>
              <span className="text-lg font-semibold text-charcoal">{dashboard.thisWeekCount}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Donation Type Breakdown */}
      <div className="bg-white border border-surface-border rounded p-6 mb-6">
        <h3 className="text-lg font-semibold text-charcoal mb-4">Donation Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-surface-linen rounded p-4">
            <div className="text-sm text-charcoal-muted mb-1">One-Time</div>
            <div className="text-2xl font-bold text-charcoal">{dashboard.oneTimeCount}</div>
            <div className="text-xs text-charcoal-muted mt-1">
              {dashboard.totalCount > 0 
                ? `${((dashboard.oneTimeCount / dashboard.totalCount) * 100).toFixed(1)}% of total`
                : '0%'}
            </div>
          </div>
          <div className="bg-surface-linen rounded p-4">
            <div className="text-sm text-charcoal-muted mb-1">Recurring</div>
            <div className="text-2xl font-bold text-charcoal">{dashboard.recurringCount}</div>
            <div className="text-xs text-charcoal-muted mt-1">
              {dashboard.totalCount > 0 
                ? `${((dashboard.recurringCount / dashboard.totalCount) * 100).toFixed(1)}% of total`
                : '0%'}
            </div>
          </div>
          <div className="bg-surface-linen rounded p-4">
            <div className="text-sm text-charcoal-muted mb-1">Active Recurring</div>
            <div className="text-2xl font-bold text-charcoal">{dashboard.activeRecurringCount}</div>
            <div className="text-xs text-charcoal-muted mt-1">
              {dashboard.recurringCount > 0 
                ? `${((dashboard.activeRecurringCount / dashboard.recurringCount) * 100).toFixed(1)}% active`
                : '0 active'}
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Line Graph */}
      <div className="bg-white border border-surface-border rounded p-6 mb-6">
        <h3 className="text-lg font-semibold text-charcoal mb-4">Weekly Donations Trend</h3>
        {dashboard.weeklyBreakdown && dashboard.weeklyBreakdown.length > 0 ? (
          <WeeklyLineChart data={dashboard.weeklyBreakdown} formatCurrency={formatCurrency} />
        ) : (
          <p className="text-charcoal-muted text-center py-8">No weekly data available</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Monthly Breakdown Chart */}
        <div className="bg-white border border-surface-border rounded p-6">
          <h3 className="text-lg font-semibold text-charcoal mb-4">Monthly Trends (Last 12 Months)</h3>
          {dashboard.monthlyBreakdown && dashboard.monthlyBreakdown.length > 0 ? (
            <div className="space-y-3">
              {dashboard.monthlyBreakdown.slice().reverse().map((month, index) => {
                const maxAmount = Math.max(...dashboard.monthlyBreakdown.map(m => m.amount))
                const percentage = maxAmount > 0 ? (month.amount / maxAmount) * 100 : 0
                return (
                  <div key={`${month.year}-${month.month}`} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-charcoal-muted">{month.month} {month.year}</span>
                      <span className="text-charcoal font-semibold">{formatCurrency(month.amount)}</span>
                    </div>
                    <div className="w-full bg-surface-linen rounded-full h-2">
                      <div
                        className="bg-gold h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="text-xs text-charcoal-muted">
                      {month.count} {month.count === 1 ? 'donation' : 'donations'}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-charcoal-muted text-center py-8">No monthly data available</p>
          )}
        </div>

        {/* Top Donors */}
        <div className="bg-white border border-surface-border rounded p-6">
          <h3 className="text-lg font-semibold text-charcoal mb-4">Top Donors</h3>
          {dashboard.topDonors && dashboard.topDonors.length > 0 ? (
            <div className="space-y-3">
              {dashboard.topDonors.map((donor, index) => (
                <div key={donor.userId} className="bg-surface-linen rounded p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gold/15 flex items-center justify-center text-charcoal font-bold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-semibold text-charcoal">{donor.userName}</div>
                        <div className="text-xs text-charcoal-muted">{donor.userEmail}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-charcoal">{formatCurrency(donor.totalAmount)}</div>
                      <div className="text-xs text-charcoal-muted">
                        {donor.donationCount} {donor.donationCount === 1 ? 'donation' : 'donations'}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-charcoal-muted text-center py-8">No donor data available</p>
          )}
        </div>
      </div>

      {/* Totals per dropdown option — succeeded donations only, net of fees */}
      {fieldSummary.length > 0 && (
        <div
          data-testid="donation-field-totals"
          className="bg-white border border-surface-border rounded p-6 mb-6"
        >
          <h3 className="text-lg font-semibold text-charcoal mb-1">Totals by field</h3>
          <p className="text-xs text-charcoal-muted mb-4">
            Completed{' '}
            {donationTypeFilter === 'all'
              ? ''
              : `${donationTypeFilter === 'roundup' ? 'round-up' : donationTypeFilter} `}
            donations only, net of fees.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fieldSummary.map(field => (
              <div key={field.fieldId} className="bg-surface-linen rounded p-4">
                <div className="text-sm font-semibold text-charcoal mb-2">
                  {field.label}{field.archived ? ' (archived)' : ''}
                </div>
                <div className="space-y-1">
                  {field.options.map(option => (
                    <div key={`${field.fieldId}-${option.value}`} className="text-sm text-charcoal-muted">
                      {option.label} — {option.count} {option.count === 1 ? 'gift' : 'gifts'}, {formatDollars(option.totalCents)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter the list by a donor's answer */}
      {filterableFields.length > 0 && (
        <div data-testid="custom-field-filter-row" className="bg-white border border-surface-border rounded p-4 mb-6">
          {/* Say plainly what this filter reaches. It narrows the list and the
              export; the stat cards and the totals above it are whole-parish
              figures and do not move. */}
          <p className="text-sm text-charcoal mb-3">
            <span className="font-medium">Filters the donation list and CSV export.</span>{' '}
            Totals above cover all donations, filtered or not.
          </p>
          <div className="flex flex-wrap items-end gap-4">
            {filterableFields.map(field => (
              <div key={field.id}>
                <label
                  htmlFor={`custom-field-filter-${field.id}`}
                  className="block text-sm font-medium text-charcoal mb-2"
                >
                  {field.label}{field.archivedAt ? ' (archived)' : ''}
                </label>
                {field.type === 'SINGLE_SELECT' ? (
                  <select
                    id={`custom-field-filter-${field.id}`}
                    aria-label={`Filter by ${field.label}`}
                    value={customFieldFilter.fieldId === field.id ? customFieldFilter.value : ''}
                    onChange={(e) => selectCustomFieldFilter(field.id, e.target.value)}
                    className="px-3 py-2 bg-white border border-surface-border rounded text-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-gold"
                  >
                    <option value="">All</option>
                    {filterOptionsFor(field).map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    id={`custom-field-filter-${field.id}`}
                    aria-label={`Search ${field.label}`}
                    type="search"
                    placeholder="Contains…"
                    value={
                      textDraft.fieldId === field.id
                        ? textDraft.value
                        : customFieldFilter.fieldId === field.id
                          ? customFieldFilter.value
                          : ''
                    }
                    onChange={(e) => setTextDraft({ fieldId: field.id, value: e.target.value })}
                    className="bg-surface-linen border-surface-border text-charcoal"
                  />
                )}
              </div>
            ))}
            {customFieldFilter.fieldId && (
              <Button
                onClick={() => selectCustomFieldFilter('', '')}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Clear field filter
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Recent Donations */}
      <div className="bg-white border border-surface-border rounded p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-charcoal flex items-center gap-2">
              Recent Donations
              {loading && <Loader2 className="w-4 h-4 text-gold animate-spin" />}
            </h3>
            {/* Unfiltered, this list reads as a recent sample. Filtered, it
                reads as the ANSWER to a question — so the cap and the
                completed-only scope have to be visible, or a longer CSV looks
                like the page lost rows. */}
            {customFieldFilter.fieldId && (
              <p data-testid="filtered-list-scope" className="text-xs text-charcoal-muted mt-1">
                {activeFilterMatchCount !== null
                  ? `Showing ${recentDonations.length} of ${activeFilterMatchCount} matches (most recent first, up to 20).`
                  : `Showing ${recentDonations.length} matches (most recent first, up to 20).`}{' '}
                The CSV export includes every match, completed or not.
              </p>
            )}
          </div>
          {parishId && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.location.href = `/app/parish/donations/list?parishId=${parishId}`}
            >
              View All
            </Button>
          )}
        </div>
        {dashboard.recentDonations && dashboard.recentDonations.length > 0 ? (
          <>
            <div className="relative">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-surface-border">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-charcoal-muted">Date</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-charcoal-muted">Donor</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-charcoal-muted">Gross</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-charcoal-muted">Fees</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-charcoal-muted">Net Received</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-charcoal-muted">Type</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-charcoal-muted">Payment</th>
                      <th className="text-center py-3 px-4 text-sm font-semibold text-charcoal-muted">Refund</th>
                      {donationFieldColumns.map(column => (
                        <th
                          key={column.fieldId}
                          className="text-left py-3 px-4 text-sm font-semibold text-charcoal-muted whitespace-nowrap"
                        >
                          {column.header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllDonations ? dashboard.recentDonations : dashboard.recentDonations.slice(0, VISIBLE_DONATION_COUNT)).map((donation) => (
                      <tr key={donation.id} className="border-b border-surface-border/50 hover:bg-surface-linen/50">
                        <td className="py-3 px-4 text-sm text-charcoal-muted whitespace-nowrap">
                          {formatDateTime(donation.completedAt || donation.createdAt)}
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <div className="text-sm font-medium text-charcoal">{donation.userName}</div>
                            <div className="text-xs text-charcoal-muted">{donation.userEmail}</div>
                            {/* Expanded, the answers are real columns; keeping
                                them inline as well would print each twice. */}
                            {!showAllDonations && (donation.customFields || []).length > 0 && (
                              <div className="mt-1 text-xs text-charcoal-muted">
                                {donation.customFields.map(field => (
                                  <div key={`${donation.id}-${field.fieldId ?? field.label}`}>
                                    <span className="font-medium">{field.label}:</span> {field.value}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="text-sm text-charcoal-muted">{formatCurrency(donation.amount)}</div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="text-sm text-charcoal-muted">
                            {formatCurrency(donation.applicationFeeAmount ?? 0)}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="text-sm font-bold text-charcoal">
                            {formatCurrency(donation.netAmount ?? donation.amount)}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-block px-2 py-1 text-xs rounded ${
                            donation.donationType === 'recurring'
                              ? 'bg-gold/15 text-charcoal'
                              : donation.donationType === 'roundup'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-gold/10 text-charcoal-muted'
                          }`}>
                            {donation.donationType === 'recurring' ? 'Recurring' : donation.donationType === 'roundup' ? 'Round-Up' : 'One-Time'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {donation.paymentMethodLast4 ? (
                            <div className="flex items-center justify-center gap-1">
                              <CreditCard className="w-4 h-4 text-charcoal-muted" />
                              <span className="text-xs text-charcoal-muted">•••• {donation.paymentMethodLast4}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-charcoal-muted">N/A</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {(() => {
                            const refunded = donation.refundedAmount || 0
                            const request = latestRequestByDonation.get(donation.id)
                            const open = request && (request.status === 'requested' || request.status === 'approved')
                            const canRequest = donation.status === 'succeeded' && donation.amount - refunded > 0 && !open
                            return (
                              <div className="flex flex-col items-center gap-1">
                                {donation.status === 'refunded' ? (
                                  <span className="inline-block px-2 py-1 text-xs rounded bg-gray-100 text-charcoal-muted">Refunded</span>
                                ) : refunded > 0 ? (
                                  <span className="inline-block px-2 py-1 text-xs rounded bg-gray-100 text-charcoal-muted">
                                    Partially refunded · {formatCurrency(refunded)}
                                  </span>
                                ) : null}
                                {donation.status !== 'refunded' && request && (
                                  <span
                                    title={request.reviewNote || request.error || undefined}
                                    className="inline-block px-2 py-1 text-xs rounded bg-gold/10 text-charcoal-muted"
                                  >
                                    {REQUEST_STATUS_LABELS[request.status] || request.status}
                                  </span>
                                )}
                                {canRequest && (
                                  <Button variant="outline" size="sm" onClick={() => setRefundTarget(donation)}>
                                    Request refund
                                  </Button>
                                )}
                              </div>
                            )
                          })()}
                        </td>
                        {donationFieldColumns.map(column => {
                          const answer = (donation.customFields || []).find(v => v.fieldId === column.fieldId)
                          return (
                            <td key={column.fieldId} className="py-3 px-4 text-sm text-charcoal-muted">
                              {answer ? answer.value : ''}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!showAllDonations && dashboard.recentDonations.length > VISIBLE_DONATION_COUNT && (
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
              )}
            </div>
            {dashboard.recentDonations.length > VISIBLE_DONATION_COUNT && (
              <button
                onClick={() => setShowAllDonations(!showAllDonations)}
                className="w-full mt-3 py-2 text-sm font-semibold text-gold hover:text-gold/80 transition-colors flex items-center justify-center gap-1.5"
              >
                {showAllDonations ? (
                  <><ChevronUp className="w-4 h-4" />Show less</>
                ) : (
                  <><ChevronDown className="w-4 h-4" />Show all {dashboard.recentDonations.length} donations</>
                )}
              </button>
            )}
          </>
        ) : (
          <p className="text-charcoal-muted text-center py-8">No recent donations</p>
        )}
      </div>
      {refundTarget && (
        <RefundRequestModal
          donation={refundTarget}
          onClose={() => setRefundTarget(null)}
          onSubmit={handleRefundSubmit}
        />
      )}
    </div>
  )
}
